import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Building2, UserPlus, Wallet, CheckCircle2, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetupWizard = ({ onSetupComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    company_name: '',
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'admin',
    cash_balance: '',
    bank_balance: '',
  });

  const update = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const validateStep1 = () => {
    if (!data.company_name.trim()) return 'Please enter your company name';
    return null;
  };
  const validateStep2 = () => {
    if (!data.username.trim()) return 'Username is required';
    if (!data.email.trim() || !/^\S+@\S+\.\S+$/.test(data.email)) return 'Please enter a valid email';
    if (!data.password || data.password.length < 6) return 'Password must be at least 6 characters';
    if (data.password !== data.confirm_password) return 'Passwords do not match';
    return null;
  };
  const validateStep3 = () => {
    const c = parseFloat(data.cash_balance);
    const b = parseFloat(data.bank_balance);
    if (Number.isNaN(c) || c < 0) return 'Please enter a valid opening cash balance (0 or more)';
    if (Number.isNaN(b) || b < 0) return 'Please enter a valid opening bank balance (0 or more)';
    return null;
  };

  const next = () => {
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) return toast.error(err);
    setStep(step + 1);
  };
  const back = () => setStep(step - 1);

  const submit = async () => {
    const err = validateStep3();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const payload = {
        company_name: data.company_name.trim(),
        username: data.username.trim(),
        email: data.email.trim(),
        password: data.password,
        role: data.role,
        cash_balance: parseFloat(data.cash_balance) || 0,
        bank_balance: parseFloat(data.bank_balance) || 0,
      };
      const res = await axios.post(`${API}/setup/initialize`, payload);
      toast.success('System initialized successfully! Welcome aboard.');
      // Auto-login using returned token
      onSetupComplete(res.data.user, res.data.access_token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Welcome to your Business Manager</h1>
          <p className="text-slate-600">Let&apos;s set up your system in under a minute.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="setup-wizard-stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= s ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-200 text-slate-500'
              }`}>{step > s ? <CheckCircle2 className="h-5 w-5" /> : s}</div>
              {s < 3 && <div className={`h-1 w-10 sm:w-16 rounded ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-0 shadow-xl">
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" /> Company Details</CardTitle>
                <CardDescription>Tell us about your business.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input id="company_name" data-testid="setup-company-name" value={data.company_name}
                    onChange={(e) => update('company_name', e.target.value)} placeholder="e.g. SCARTIO Pvt Ltd" />
                </div>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-blue-600" /> Administrator Account</CardTitle>
                <CardDescription>Create the first account. You can add more users later.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input id="username" data-testid="setup-username" value={data.username}
                      onChange={(e) => update('username', e.target.value)} placeholder="admin" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid="setup-email" value={data.email}
                      onChange={(e) => update('email', e.target.value)} placeholder="admin@company.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" data-testid="setup-password" value={data.password}
                      onChange={(e) => update('password', e.target.value)} placeholder="At least 6 characters" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm Password</Label>
                    <Input id="confirm_password" type="password" data-testid="setup-confirm-password" value={data.confirm_password}
                      onChange={(e) => update('confirm_password', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={data.role} onValueChange={(v) => update('role', v)}>
                    <SelectTrigger data-testid="setup-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin (full access)</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">We recommend starting as an Admin.</p>
                </div>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" /> Opening Balances</CardTitle>
                <CardDescription>Enter your current cash and bank balances. These will update automatically as you record transactions.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cash_balance">Cash on Hand (LKR)</Label>
                    <Input id="cash_balance" type="number" step="0.01" data-testid="setup-cash-balance" value={data.cash_balance}
                      onChange={(e) => update('cash_balance', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_balance">Bank Balance (LKR)</Label>
                    <Input id="bank_balance" type="number" step="0.01" data-testid="setup-bank-balance" value={data.bank_balance}
                      onChange={(e) => update('bank_balance', e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-sm text-slate-700">
                  <p className="font-medium text-blue-900 mb-1">What happens next?</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600">
                    <li>An admin account will be created.</li>
                    <li>Your opening balances will be saved and kept up-to-date automatically.</li>
                    <li>You&apos;ll be logged in and taken straight to your dashboard.</li>
                  </ul>
                </div>
              </CardContent>
            </>
          )}

          <div className="flex items-center justify-between p-6 pt-0 border-t mt-4 pt-6">
            <Button variant="outline" onClick={back} disabled={step === 1 || loading} data-testid="setup-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < 3 ? (
              <Button onClick={next} data-testid="setup-next-btn" className="bg-blue-600 hover:bg-blue-700">
                Continue <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading} data-testid="setup-finish-btn" className="bg-gradient-to-r from-blue-600 to-indigo-600">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting up...</> : <>Finish Setup <CheckCircle2 className="h-4 w-4 ml-2" /></>}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SetupWizard;
